import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { vendorAuthApi } from "../../services/auth";
import { useVendorAuth } from "../../store/vendorAuthStore";
import "../Auth/auth.css";

const isEmpty = (v) => !String(v || "").trim();
const CATEGORIES = ["Grocery", "Fashion", "Electronics", "Home & Kitchen", "Beauty", "Others"];
const STEP_TITLES = ["Basic Info", "Address", "Bank Details", "Products", "Delivery", "Agreement", "Documents"];

const BASE_URL = "https://xiadot.com/vendor_api/";

function Field({ label, children }) {
  return (
    <div className="ep-field">
      <label className="ep-label">{label}</label>
      {children}
    </div>
  );
}

function RadioGroup({ options, value, onChange }) {
  return (
    <div className="ep-radio-group">
      {options.map((x) => (
        <label key={x} className={`ep-radio-opt${value === x ? " active" : ""}`}>
          <input type="radio" checked={value === x} onChange={() => onChange(x)} />
          {x}
        </label>
      ))}
    </div>
  );
}

function FileField({ label, fieldKey, form, setVal, accept = "image/*,.pdf" }) {
  const existingUrl = form[`${fieldKey}_url`];
  const newFile = form[fieldKey];
  const isImage = existingUrl && /\.(jpg|jpeg|png|webp|gif)$/i.test(existingUrl);
  const isPdf = existingUrl && /\.pdf$/i.test(existingUrl);
  const fileName = existingUrl ? existingUrl.split("/").pop() : "";

  return (
    <Field label={label}>
      {existingUrl && !newFile && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          marginBottom: 8, padding: "8px 10px",
          background: "var(--color-background-secondary)",
          border: "0.5px solid var(--color-border-tertiary)",
          borderRadius: 8, fontSize: 13
        }}>
          {isImage ? (
            <img
              src={BASE_URL + existingUrl}
              alt="existing"
              style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, flexShrink: 0 }}
            />
          ) : (
            <span style={{ fontSize: 20 }}>{isPdf ? "📄" : "📎"}</span>
          )}
          <div>
            <div style={{ color: "var(--color-text-primary)", fontWeight: 500, fontSize: 12 }}>
              {fileName}
            </div>
            <div style={{ color: "var(--color-text-secondary)", fontSize: 11, marginTop: 2 }}>
              Current file — upload new to replace
            </div>
          </div>
        </div>
      )}

      {newFile && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          marginBottom: 6, fontSize: 13,
          color: "var(--color-text-secondary)"
        }}>
          <span style={{ color: "#16a34a", fontWeight: 500 }}>✓</span>
          New: {newFile.name}
          <button
            type="button"
            onClick={() => setVal(fieldKey, null)}
            style={{
              marginLeft: 4, fontSize: 11, cursor: "pointer",
              color: "var(--color-text-secondary)", background: "none",
              border: "none", padding: 0, textDecoration: "underline"
            }}
          >
            Cancel
          </button>
        </div>
      )}

      <input
        className="ep-file"
        type="file"
        accept={accept}
        onChange={e => setVal(fieldKey, e.target.files?.[0] || null)}
      />
    </Field>
  );
}

// ✅ Helper: maps a raw DB vendor row into the form state shape
function vendorToForm(v) {
  return {
    shop_name: v.shop_name || "",
    owner_name: v.owner_name || "",
    email: v.email || "",
    phone: v.phone || "",
    gst: v.gst || "",
    business_type: v.business_type || "",
    pan: v.pan || "",
    udyam: v.udyam || "",
    years_in_business: v.years_in_business || "",
    address: v.address || "",
    district: v.district || "",
    state: v.state || "",
    pincode: v.pincode || "",
    landmark: v.landmark || "",
    pickup_address: v.pickup_address || "",
    account_holder: v.account_holder || "",
    bank_name: v.bank_name || "",
    account_no: v.account_no || "",
    ifsc: v.ifsc || "",
    upi_id: v.upi_id || "",
    product_category: v.product_category
      ? (Array.isArray(v.product_category)
        ? v.product_category
        : v.product_category.split(",").map(s => s.trim()).filter(Boolean))
      : [],
    approx_products: v.approx_products || "",
    brand_name: v.brand_name || "",
    sell_type: v.sell_type || "",
    provide_delivery: v.provide_delivery || "",
    delivery_model: v.delivery_model || "",
    order_processing_time: v.order_processing_time || "",
    working_hours: v.working_hours || "",
    commission_pct: v.commission_pct || "",
    settlement_cycle: v.settlement_cycle || "",
    agree_terms:
      v.agree_terms === true ||
      v.agree_terms === 1 ||
      v.agree_terms === "1" ||
      v.agree_terms === "true",
    signature: v.signature || "",
    date: v.date || new Date().toISOString().split("T")[0],
    // new file uploads (null until user picks a file)
    gst_certificate: null,
    pan_card: null,
    aadhaar_card: null,
    cancelled_cheque: null,
    shop_photo: null,
    product_images: null,
    // existing file URLs from DB — shown as previews
    gst_certificate_url: v.gst_certificate || "",
    pan_card_url: v.pan_card || "",
    aadhaar_card_url: v.aadhaar_card || "",
    cancelled_cheque_url: v.cancelled_cheque || "",
    shop_photo_url: v.shop_photo || "",
    product_images_urls: v.product_images
      ? (Array.isArray(v.product_images)
        ? v.product_images
        : v.product_images.split(",").map(s => s.trim()).filter(Boolean))
      : [],
  };
}

const EMPTY_FORM = vendorToForm({});

export default function EditProfile() {
  const nav = useNavigate();
  const { vendor, token, setVendor } = useVendorAuth();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);  // ✅ loading state while fetching DB
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);

  // ✅ On mount: fetch FULL vendor row directly from DB
  useEffect(() => {
    const vendorId =
      vendor?.id ||
      vendor?.vendor_id ||
      (() => {
        try {
          return JSON.parse(localStorage.getItem("vendor_me") || "{}")?.id || "";
        } catch { return ""; }
      })();

    const authToken =
      token ||
      localStorage.getItem("vendor_token") ||
      "";

    if (!vendorId) {
      setFetching(false);
      return;
    }

    (async () => {
      try {
        const v = await vendorAuthApi.getProfile(vendorId, authToken);
        setForm(vendorToForm(v));
        // ✅ Also sync Zustand + localStorage with fresh full data
        setVendor(v);
      } catch (e) {
        console.warn("getProfile failed, falling back to cached vendor:", e.message);
        // Fallback: use whatever is in Zustand / localStorage
        const fallback = vendor ||
          (() => {
            try { return JSON.parse(localStorage.getItem("vendor_me") || "{}"); }
            catch { return {}; }
          })();
        if (fallback?.id) setForm(vendorToForm(fallback));
      } finally {
        setFetching(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setVal = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setText = (k) => (e) => setVal(k, e.target.value);

  const validateStep = () => {
    setErr(""); setOk("");
    if (step === 1) {
      if (isEmpty(form.shop_name)) { setErr("Shop Name required"); return false; }
      if (isEmpty(form.owner_name)) { setErr("Owner Name required"); return false; }
      if (isEmpty(form.email)) { setErr("Email required"); return false; }
      if (isEmpty(form.phone)) { setErr("Phone required"); return false; }
      if (isEmpty(form.business_type)) { setErr("Business Type select pannanum"); return false; }
    }
    if (step === 2) {
      if (isEmpty(form.address)) { setErr("Address required"); return false; }
      if (isEmpty(form.district)) { setErr("District required"); return false; }
      if (isEmpty(form.state)) { setErr("State required"); return false; }
      if (isEmpty(form.pincode)) { setErr("Pincode required"); return false; }
    }
    if (step === 3) {
      if (isEmpty(form.account_holder)) { setErr("Account Holder Name required"); return false; }
      if (isEmpty(form.bank_name)) { setErr("Bank Name required"); return false; }
      if (isEmpty(form.account_no)) { setErr("Account Number required"); return false; }
      if (isEmpty(form.ifsc)) { setErr("IFSC Code required"); return false; }
    }
    if (step === 4) {
      if (!form.product_category?.length) { setErr("Product Category select pannanum"); return false; }
      if (isEmpty(form.approx_products)) { setErr("Approx No. of Products required"); return false; }
      if (isEmpty(form.sell_type)) { setErr("Do you sell select pannanum"); return false; }
    }
    if (step === 5) {
      if (isEmpty(form.provide_delivery)) { setErr("Delivery Yes/No select pannanum"); return false; }
      if (form.provide_delivery === "Yes" && isEmpty(form.delivery_model)) {
        setErr("Delivery Model select pannanum"); return false;
      }
      if (isEmpty(form.order_processing_time)) { setErr("Order Processing Time required"); return false; }
    }
    if (step === 6) {
      if (isEmpty(form.settlement_cycle)) { setErr("Settlement Cycle select pannanum"); return false; }
      if (!form.agree_terms) { setErr("Agreement accept pannanum"); return false; }
    }
    return true;
  };

  const next = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!validateStep()) return;
    setStep(s => Math.min(7, s + 1));
  };

  const back = (e) => {
    e.preventDefault(); e.stopPropagation();
    setErr(""); setOk("");
    setStep(s => Math.max(1, s - 1));
  };

  const toggleCategory = (name) => {
    const set = new Set(form.product_category || []);
    set.has(name) ? set.delete(name) : set.add(name);
    setVal("product_category", Array.from(set));
  };

  const submit = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setLoading(true); setErr(""); setOk("");

    try {
      const vendorId =
        vendor?.id || vendor?.vendor_id ||
        JSON.parse(localStorage.getItem("vendor_me") || "{}")?.id || "";

      if (!vendorId) {
        setErr("Vendor ID not found. Please log in again.");
        setLoading(false);
        return;
      }

      const fd = new FormData();
      fd.append("vendor_id", String(vendorId));

      const textFields = [
        "shop_name", "owner_name", "email", "phone", "gst",
        "business_type", "pan", "udyam", "years_in_business",
        "address", "district", "state", "pincode", "landmark",
        "pickup_address", "account_holder", "bank_name", "account_no",
        "ifsc", "upi_id", "approx_products", "brand_name", "sell_type",
        "provide_delivery", "delivery_model", "order_processing_time",
        "working_hours", "settlement_cycle",
        "agree_terms", "signature", "date"
      ];
      textFields.forEach(key => {
        if (key === "agree_terms") {
          fd.append("agree_terms", form.agree_terms ? "1" : "0");
        } else {
          const val = form[key];
          fd.append(key, val !== null && val !== undefined ? String(val) : "");
        }
      });
      fd.append("product_category", (form.product_category || []).join(", "));

      const singleFileFields = ["gst_certificate", "pan_card", "aadhaar_card", "cancelled_cheque", "shop_photo"];
      singleFileFields.forEach(key => {
        if (form[key] instanceof File) {
          fd.append(key, form[key]);
        } else if (form[`${key}_url`]) {
          fd.append(`${key}_existing`, form[`${key}_url`]);
        }
      });

      if (form.product_images instanceof FileList && form.product_images.length > 0) {
        for (let i = 0; i < form.product_images.length; i++) {
          fd.append("product_images[]", form.product_images[i]);
        }
      } else if (form.product_images_urls?.length > 0) {
        fd.append("product_images_existing", form.product_images_urls.join(","));
      }

      const res = await vendorAuthApi.updateProfile(fd);

      if (res?.success) {
        setOk("Profile updated successfully! Redirecting...");

        // ✅ Refetch full fresh data from DB and sync everywhere
        try {
          const fresh = await vendorAuthApi.getProfile(vendorId, token);
          setVendor(fresh);
          localStorage.setItem("vendor_me", JSON.stringify(fresh));
        } catch (_) { }

        setTimeout(() => nav("/dashboard"), 1500);
      } else {
        setErr(res?.message || "Update failed. Please try again.");
      }
    } catch (ex) {
      console.error("Submit error:", ex);
      setErr(ex.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Show loading spinner while fetching from DB
  if (fetching) {
    return (
      <div className="ep-page">
        <div className="ep-head">
          <div className="ep-icon-box">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div>
            <h2 className="ep-title">Edit Profile</h2>
            <div className="ep-sub">Loading your profile...</div>
          </div>
        </div>
        <div className="ep-card" style={{ textAlign: "center", padding: 48, color: "var(--color-text-secondary)" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
          <div>Loading your profile data from server...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ep-page">
      <div className="ep-head">
        <div className="ep-icon-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>
        <div>
          <h2 className="ep-title">Edit Profile</h2>
          <div className="ep-sub">Update your vendor information</div>
        </div>
      </div>

      <div className="ep-card">
        <div className="ep-step-bar">
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i}
              className={`ep-step-dot${i + 1 === step ? " active" : i + 1 < step ? " done" : ""}`}
            />
          ))}
        </div>
        <div className="ep-step-label">
          Step <strong>{step} of 7</strong> — <strong>{STEP_TITLES[step - 1]}</strong>
        </div>

        {err && <div className="v-alert v-alert-error">{err}</div>}
        {ok && <div className="v-alert v-alert-ok">{ok}</div>}

        <form onSubmit={submit}>

          {step === 1 && (
            <>
              <div className="ep-section-title">Basic Information</div>
              <div className="ep-grid2">
                <Field label="Shop Name">
                  <input className="ep-input" value={form.shop_name} onChange={setText("shop_name")} placeholder="Enter shop name" />
                </Field>
                <Field label="Owner Name">
                  <input className="ep-input" value={form.owner_name} onChange={setText("owner_name")} placeholder="Enter owner name" />
                </Field>
                <Field label="Email">
                  <input className="ep-input" type="email" value={form.email} onChange={setText("email")} placeholder="Enter email" />
                </Field>
                <Field label="Phone">
                  <input className="ep-input" value={form.phone} onChange={setText("phone")} placeholder="Enter phone" />
                </Field>
                <Field label="GST (optional)">
                  <input className="ep-input" value={form.gst} onChange={setText("gst")} placeholder="Enter GST number" />
                </Field>
                <Field label="PAN Number">
                  <input className="ep-input" value={form.pan} onChange={setText("pan")} placeholder="Enter PAN number" />
                </Field>
                <Field label="UDYAM/MSME (optional)">
                  <input className="ep-input" value={form.udyam} onChange={setText("udyam")} placeholder="Enter UDYAM number" />
                </Field>
                <Field label="Years in Business">
                  <input className="ep-input" value={form.years_in_business} onChange={setText("years_in_business")} placeholder="e.g. 3" />
                </Field>
              </div>
              <div className="ep-divider" />
              <Field label="Business Type">
                <RadioGroup
                  options={["Individual", "Proprietorship", "Partnership", "LLP", "Pvt Ltd"]}
                  value={form.business_type}
                  onChange={v => setVal("business_type", v)}
                />
              </Field>
            </>
          )}

          {step === 2 && (
            <>
              <div className="ep-section-title">Shop / Office Address</div>
              <Field label="Full Address">
                <input className="ep-input" value={form.address} onChange={setText("address")} placeholder="Enter full address" />
              </Field>
              <div className="ep-grid2">
                <Field label="District">
                  <input className="ep-input" value={form.district} onChange={setText("district")} placeholder="Enter district" />
                </Field>
                <Field label="State">
                  <input className="ep-input" value={form.state} onChange={setText("state")} placeholder="Enter state" />
                </Field>
                <Field label="Pincode">
                  <input className="ep-input" value={form.pincode} onChange={setText("pincode")} placeholder="Enter pincode" />
                </Field>
                <Field label="Landmark">
                  <input className="ep-input" value={form.landmark} onChange={setText("landmark")} placeholder="Enter landmark" />
                </Field>
              </div>
              <Field label="Pickup Address">
                <input className="ep-input" value={form.pickup_address} onChange={setText("pickup_address")} placeholder="Enter pickup address" />
              </Field>
            </>
          )}

          {step === 3 && (
            <>
              <div className="ep-section-title">Bank Details</div>
              <div className="ep-grid2">
                <Field label="Account Holder Name">
                  <input className="ep-input" value={form.account_holder} onChange={setText("account_holder")} placeholder="Enter holder name" />
                </Field>
                <Field label="Bank Name">
                  <input className="ep-input" value={form.bank_name} onChange={setText("bank_name")} placeholder="Enter bank name" />
                </Field>
                <Field label="Account Number">
                  <input className="ep-input" value={form.account_no} onChange={setText("account_no")} placeholder="Enter account number" />
                </Field>
                <Field label="IFSC Code">
                  <input className="ep-input" value={form.ifsc} onChange={setText("ifsc")} placeholder="Enter IFSC code" />
                </Field>
                <Field label="UPI ID (optional)">
                  <input className="ep-input" value={form.upi_id} onChange={setText("upi_id")} placeholder="Enter UPI ID" />
                </Field>
              </div>
              <div className="ep-divider" />
              <FileField label="Cancelled Cheque" fieldKey="cancelled_cheque" form={form} setVal={setVal} />
            </>
          )}

          {step === 4 && (
            <>
              <div className="ep-section-title">Product Information</div>
              <Field label="Product Category">
                <div className="ep-radio-group">
                  {CATEGORIES.map(c => {
                    const active = (form.product_category || []).includes(c);
                    return (
                      <label key={c} className={`ep-radio-opt${active ? " active" : ""}`}>
                        <input type="checkbox" style={{ display: "none" }}
                          checked={active} onChange={() => toggleCategory(c)} />
                        {c}
                      </label>
                    );
                  })}
                </div>
              </Field>
              <div className="ep-grid2">
                <Field label="Approx No. of Products">
                  <input className="ep-input" value={form.approx_products} onChange={setText("approx_products")} placeholder="e.g. 50" />
                </Field>
                <Field label="Brand Name (optional)">
                  <input className="ep-input" value={form.brand_name} onChange={setText("brand_name")} placeholder="Enter brand name" />
                </Field>
              </div>
              <div className="ep-divider" />
              <Field label="Do you sell">
                <RadioGroup
                  options={["Own Brand", "Reseller", "Both"]}
                  value={form.sell_type}
                  onChange={v => setVal("sell_type", v)}
                />
              </Field>
            </>
          )}

          {step === 5 && (
            <>
              <div className="ep-section-title">Delivery & Operations</div>
              <Field label="Do you provide delivery?">
                <RadioGroup options={["Yes", "No"]} value={form.provide_delivery} onChange={v => setVal("provide_delivery", v)} />
              </Field>
              {form.provide_delivery === "Yes" && (
                <Field label="Preferred Delivery Model">
                  <RadioGroup options={["Self Delivery", "Platform Delivery"]} value={form.delivery_model} onChange={v => setVal("delivery_model", v)} />
                </Field>
              )}
              <div className="ep-grid2">
                <Field label="Average Order Processing Time">
                  <input className="ep-input" value={form.order_processing_time} onChange={setText("order_processing_time")} placeholder="e.g. 24 hours" />
                </Field>
                <Field label="Working Hours">
                  <input className="ep-input" value={form.working_hours} onChange={setText("working_hours")} placeholder="e.g. 9am - 6pm" />
                </Field>
              </div>
            </>
          )}

          {step === 6 && (
            <>
              <div className="ep-section-title">Agreement</div>
              <div className="ep-grid2">
                <Field label="Signature">
                  <input className="ep-input" value={form.signature} onChange={setText("signature")} placeholder="Enter your name" />
                </Field>
                <Field label="Date">
                  <input className="ep-input" type="date" value={form.date} onChange={setText("date")} />
                </Field>
              </div>
              <div className="ep-divider" />
              <Field label="Payment Settlement Cycle">
                <RadioGroup options={["7 Days", "15 Days"]} value={form.settlement_cycle} onChange={v => setVal("settlement_cycle", v)} />
              </Field>
              <div className="ep-field" style={{ marginTop: 8 }}>
                <label className="ep-check-label">
                  <input type="checkbox" checked={!!form.agree_terms}
                    onChange={e => setVal("agree_terms", e.target.checked)} />
                  Seller agrees to Return/Refund policies, penalties, and quality standards of YURO SHOPY.
                </label>
              </div>
            </>
          )}

          {step === 7 && (
            <>
              <div className="ep-section-title">Document Uploads</div>
              <div className="ep-grid2">
                <FileField label="GST Certificate (optional)" fieldKey="gst_certificate" form={form} setVal={setVal} />
                <FileField label="PAN Card" fieldKey="pan_card" form={form} setVal={setVal} />
                <FileField label="Aadhaar Card" fieldKey="aadhaar_card" form={form} setVal={setVal} />
                <FileField label="Shop Photo" fieldKey="shop_photo" form={form} setVal={setVal} />

                <Field label="Product Sample Images">
                  {form.product_images_urls?.length > 0 && !form.product_images && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                        {form.product_images_urls.map((url, i) => (
                          <img key={i} src={BASE_URL + url} alt={`product ${i + 1}`}
                            style={{
                              width: 48, height: 48, objectFit: "cover", borderRadius: 6,
                              border: "0.5px solid var(--color-border-tertiary)"
                            }}
                          />
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                        {form.product_images_urls.length} existing image(s) — upload new to replace all
                      </div>
                    </div>
                  )}
                  {form.product_images instanceof FileList && form.product_images.length > 0 && (
                    <div style={{
                      fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 6,
                      display: "flex", alignItems: "center", gap: 6
                    }}>
                      <span style={{ color: "#16a34a", fontWeight: 500 }}>✓</span>
                      {form.product_images.length} new image(s) selected
                      <button type="button" onClick={() => setVal("product_images", null)}
                        style={{
                          fontSize: 11, cursor: "pointer", color: "var(--color-text-secondary)",
                          background: "none", border: "none", padding: 0, textDecoration: "underline"
                        }}>
                        Cancel
                      </button>
                    </div>
                  )}
                  <input className="ep-file" type="file" multiple accept="image/*"
                    onChange={e => setVal("product_images", e.target.files?.length ? e.target.files : null)} />
                </Field>
              </div>
            </>
          )}

          <div className="ep-footer">
            {step > 1 && (
              <button type="button" className="ep-back-btn" onClick={back} disabled={loading}>
                ← Back
              </button>
            )}
            {step < 7 ? (
              <button type="button" className="ep-next-btn" onClick={next} disabled={loading}>
                Next →
              </button>
            ) : (
              <button type="submit" className="ep-next-btn" disabled={loading}>
                {loading ? "Saving..." : "Update Profile →"}
              </button>
            )}
          </div>

        </form>
      </div>
    </div>
  );
}